import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/apiService';
import { showToast } from '../components/Toast';
import BottomSheet from '../components/BottomSheet';
import { DSBackLink, DSButton, DSInput, DSSelect, DSCard, DSTypeaheadFilterSelect } from '../design-system/primitives';
import PinGolfBottomNav from '../components/PinGolfBottomNav';
import pingolfHero from '../assets/pingolf-hero.webp';
import './PinGolfLeagueAdmin.css';
import { personName } from '../utils/personName';

// Setting a season up used to be two disconnected halves: this form asked for the number, the
// scoring rule and the length, name-dropped the money settings without offering them, and then
// dropped you on the season page to find the buy-in, the start date, the host slots and the
// schedule build-out yourself. Everything a new season needs now lives here in order, and the
// season page keeps the same panels for editing any of it later.
const STEPS = [
  { key: 'basics', title: 'Basics' },
  { key: 'money', title: 'Money' },
  { key: 'schedule', title: 'Schedule' },
  { key: 'review', title: 'Review' }
];

// Which of a player's cards counts for the week. Locked in at creation, since changing it
// mid-season would rescore weeks that were already played under the other rule.
const CARD_SCORING_MODES = [
  {
    value: 'latest',
    label: 'Latest card counts',
    blurb: 'Buying a card replaces the one before it, better or worse. Unplayed holes on a card score +7 each, so walking away from a bad replay still counts.'
  },
  {
    value: 'best',
    label: 'Best card counts',
    blurb: 'Your lowest card of the week counts. Extra cards can only help you, so there is no penalty for an unfinished one.'
  }
];

// Server defaults (server/src/config/pingolfSeason.js). Mirrored here so the form opens on the
// same numbers the season would have been created with.
const DEFAULT_WEEK_COUNT = 8;
const DEFAULT_DROP_COUNT = 2;
const DEFAULT_CARD_PRICE_DOLLARS = '5';
const DEFAULT_SEASON_POOL_PER_CARD = '1';
const DEFAULT_WEEKLY_FLOOR_DOLLARS = '5';
const DEFAULT_PAYOUT_STRUCTURE = 'top_four_40_25_20_15';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const PLACE_LABELS = ['1st', '2nd', '3rd', '4th', '5th', '6th'];

const SEASON_PRIZE_LABELS = {
  champion: 'Champion',
  runner_up: 'Runner-up',
  third: 'Third',
  fourth: 'Fourth'
};

// Season prize order is by place, and object key order is not guaranteed to match it.
const SEASON_PRIZE_ORDER = ['champion', 'runner_up', 'third', 'fourth'];

const orderedSeasonSplit = (split) => SEASON_PRIZE_ORDER
  .filter(key => Number(split?.[key]) > 0)
  .map(key => ({ key, label: SEASON_PRIZE_LABELS[key] || key, share: Number(split[key]) }));

const percent = (share) => `${Math.round(Number(share) * 100)}%`;

const formatMoney = (amount) => {
  const value = Number(amount) || 0;
  return `$${value.toLocaleString('en-US', {
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: 2
  })}`;
};

const hostDisplayName = (user) => personName(user)
  || user.username
  || user.email;

const formatTimeOfDay = (timeOfDay) => {
  if (!timeOfDay) return '';
  const [hourStr, minuteStr] = timeOfDay.split(':');
  const hour = parseInt(hourStr, 10);
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = ((hour + 11) % 12) + 1;
  return `${displayHour}:${minuteStr} ${period}`;
};

const formatSlotDuration = (minutes) => {
  const total = parseInt(minutes, 10);
  if (!Number.isInteger(total) || total <= 0) return 'No length set';
  const hours = Math.floor(total / 60);
  const remainder = total % 60;
  if (!hours) return `${remainder} min session`;
  return remainder ? `${hours} hr ${remainder} min session` : `${hours} hr session`;
};

// A date input hands back YYYY-MM-DD; rebuilding it in local time keeps it from rendering as
// the previous day west of UTC.
const formatStartDate = (rawDate) => {
  if (!rawDate) return '';
  const [year, month, day] = rawDate.slice(0, 10).split('-').map(Number);
  if (!year || !month || !day) return '';
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
  });
};

function PinGolfLeagueSeasonCreate() {
  const navigate = useNavigate();
  const [stepIndex, setStepIndex] = useState(0);
  const [busy, setBusy] = useState(false);
  const [progressLabel, setProgressLabel] = useState('');

  // Step 1: basics
  const [seasonNumber, setSeasonNumber] = useState('');
  const [cardScoringMode, setCardScoringMode] = useState('latest');
  const [weekCount, setWeekCount] = useState(String(DEFAULT_WEEK_COUNT));
  const [dropCount, setDropCount] = useState(String(DEFAULT_DROP_COUNT));

  // Step 2: money
  const [structures, setStructures] = useState({ weekly: [], season: [] });
  const [cardPriceDollars, setCardPriceDollars] = useState(DEFAULT_CARD_PRICE_DOLLARS);
  const [seasonPoolPerCard, setSeasonPoolPerCard] = useState(DEFAULT_SEASON_POOL_PER_CARD);
  const [weeklyFloorDollars, setWeeklyFloorDollars] = useState(DEFAULT_WEEKLY_FLOOR_DOLLARS);
  const [weeklyPayoutPreset, setWeeklyPayoutPreset] = useState(DEFAULT_PAYOUT_STRUCTURE);
  const [seasonPayoutStructure, setSeasonPayoutStructure] = useState(DEFAULT_PAYOUT_STRUCTURE);

  // Step 3: schedule. Slots are staged locally and only written once the season row exists.
  const [hosts, setHosts] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [slots, setSlots] = useState([]);
  const [showSlotModal, setShowSlotModal] = useState(false);
  const [slotDay, setSlotDay] = useState('2');
  const [slotTime, setSlotTime] = useState('18:00');
  const [slotDuration, setSlotDuration] = useState('240');
  const [slotHostId, setSlotHostId] = useState('');

  // Seasons run in order, so the next number is almost always the only answer. Prefilled
  // rather than forced: a one-off or a renumbering can still type over it.
  const prefillNextSeasonNumber = useCallback(async () => {
    try {
      const result = await apiService.getPingolfSeasons();
      const highest = (result.data || []).reduce(
        (max, season) => Math.max(max, Number(season.season_number) || 0),
        0
      );
      setSeasonNumber(String(highest + 1));
    } catch (error) {
      // A failed prefill is not worth blocking on - the field just starts empty.
      setSeasonNumber('');
    }
  }, []);

  useEffect(() => { prefillNextSeasonNumber(); }, [prefillNextSeasonNumber]);

  useEffect(() => {
    let cancelled = false;
    apiService.getPingolfPayoutStructures()
      .then(res => { if (!cancelled) setStructures(res.data); })
      .catch(() => { if (!cancelled) showToast('Could not load payout structures', 'error'); });
    apiService.get('/users?hostEligible=true')
      .then(res => { if (!cancelled) setHosts(res.data); })
      .catch(() => { if (!cancelled) showToast('Could not load hosts', 'error'); });
    return () => { cancelled = true; };
  }, []);

  const parsedWeekCount = parseInt(weekCount, 10);
  const parsedDropCount = parseInt(dropCount, 10);
  const countedWeeks = (
    Number.isInteger(parsedWeekCount) && Number.isInteger(parsedDropCount)
      && parsedDropCount >= 0 && parsedDropCount < parsedWeekCount
  ) ? parsedWeekCount - parsedDropCount : null;

  const weeklyPerCard = Math.max(Number(cardPriceDollars) - Number(seasonPoolPerCard), 0);

  const draftWeekly = useMemo(
    () => structures.weekly.find(entry => entry.key === weeklyPayoutPreset) || null,
    [structures.weekly, weeklyPayoutPreset]
  );
  const draftSeasonStructure = useMemo(
    () => structures.season.find(entry => entry.key === seasonPayoutStructure) || null,
    [structures.season, seasonPayoutStructure]
  );

  // Preview against a realistic field so the percentages read as money, not abstractions.
  const exampleField = 15;
  const examplePot = weeklyPerCard * exampleField;

  const validateBasics = () => {
    const parsedSeasonNumber = parseInt(seasonNumber, 10);
    if (!Number.isInteger(parsedSeasonNumber) || parsedSeasonNumber <= 0) {
      showToast('Enter a season number', 'warning');
      return false;
    }
    if (!Number.isInteger(parsedWeekCount) || parsedWeekCount <= 0) {
      showToast('Enter how many weeks the season runs', 'warning');
      return false;
    }
    if (!Number.isInteger(parsedDropCount) || parsedDropCount < 0) {
      showToast('Enter how many weeks are dropped', 'warning');
      return false;
    }
    if (parsedDropCount >= parsedWeekCount) {
      showToast('Dropped weeks must be fewer than the weeks played', 'warning');
      return false;
    }
    return true;
  };

  // Mirrors updateSeasonSettings on the server, so a bad number is caught on the step that owns
  // it rather than two steps later on submit.
  const validateMoney = () => {
    const price = Number(cardPriceDollars);
    if (!Number.isFinite(price) || price <= 0) {
      showToast('The card buy-in must be more than $0', 'warning');
      return false;
    }
    const perCard = Number(seasonPoolPerCard);
    if (!Number.isFinite(perCard) || perCard < 0) {
      showToast('The season pool share cannot be negative', 'warning');
      return false;
    }
    if (perCard >= price) {
      showToast('The season pool share must be less than the buy-in, or no money reaches the weekly pot', 'warning');
      return false;
    }
    const floor = Number(weeklyFloorDollars);
    if (!Number.isFinite(floor) || floor < 0) {
      showToast('The weekly floor cannot be negative', 'warning');
      return false;
    }
    if (!weeklyPayoutPreset || !seasonPayoutStructure) {
      showToast('Pick both payout structures', 'warning');
      return false;
    }
    return true;
  };

  // The schedule step is optional on purpose: the start date is often not settled when the
  // season is being set up, and the season page can still do all of this later.
  const validateSchedule = () => {
    if (!startDate && slots.length > 0) {
      showToast('Pick a Week 1 start date, or remove the host slots', 'warning');
      return false;
    }
    return true;
  };

  const STEP_VALIDATORS = [validateBasics, validateMoney, validateSchedule];

  const goNext = () => {
    const validate = STEP_VALIDATORS[stepIndex];
    if (validate && !validate()) return;
    setStepIndex(index => Math.min(index + 1, STEPS.length - 1));
  };

  const goBack = () => setStepIndex(index => Math.max(index - 1, 0));

  const addSlot = () => {
    if (!slotHostId) {
      showToast('Pick a host for this slot', 'warning');
      return;
    }
    setSlots(current => [...current, {
      // Staged rows have no server id yet, so a local key keeps React stable across removals.
      localId: `${current.length}-${slotDay}-${slotTime}-${slotHostId}`,
      dayOfWeek: parseInt(slotDay, 10),
      timeOfDay: slotTime,
      durationMinutes: parseInt(slotDuration, 10) || 240,
      hostUserId: slotHostId
    }]);
    setSlotHostId('');
    setShowSlotModal(false);
  };

  const removeSlot = (localId) => setSlots(current => current.filter(slot => slot.localId !== localId));

  const hostNameById = (id) => {
    const host = hosts.find(entry => String(entry.id) === String(id));
    return host ? hostDisplayName(host) : 'Unknown host';
  };

  // One submit for the whole wizard. The season row has to exist before anything else can be
  // written to it, so this is a sequence rather than a single call - and once the row exists a
  // later failure is recoverable rather than a dead end: the toast says setup did not finish and
  // the season page holds the same panels to complete it by hand.
  const createSeason = async () => {
    if (!validateBasics() || !validateMoney() || !validateSchedule()) return;

    setBusy(true);
    let seasonId = null;
    try {
      setProgressLabel('Creating season...');
      // The name is always "PinGolf League Season N", so the server derives it.
      const result = await apiService.createPingolfSeason({
        seasonNumber: parseInt(seasonNumber, 10),
        cardScoringMode,
        weekCount: parsedWeekCount,
        dropCount: parsedDropCount
      });
      seasonId = result.data.id;
    } catch (error) {
      // Nothing was created, so this stays on the wizard with the form intact.
      showToast(error.response?.data?.error || 'Failed to create season', 'error');
      setBusy(false);
      setProgressLabel('');
      return;
    }

    try {
      setProgressLabel('Saving buy-in and payouts...');
      await apiService.updatePingolfSeasonSettings(seasonId, {
        cardPriceDollars: Number(cardPriceDollars),
        seasonPoolPerCard: Number(seasonPoolPerCard),
        weeklyFloorDollars: Number(weeklyFloorDollars),
        weeklyPayoutPreset,
        seasonPayoutStructure
      });

      if (startDate) {
        setProgressLabel('Setting the start date...');
        await apiService.setPingolfSeasonStartDate(seasonId, startDate);

        for (const slot of slots) {
          setProgressLabel('Adding host slots...');
          await apiService.addPingolfHostSlot(seasonId, {
            dayOfWeek: slot.dayOfWeek,
            timeOfDay: slot.timeOfDay,
            durationMinutes: slot.durationMinutes,
            hostUserId: slot.hostUserId
          });
        }

        // Weeks come first: a host night hangs off a week, so generating nights before the
        // weeks exist would put nothing on the calendar.
        if (slots.length > 0) {
          setProgressLabel('Generating weeks and host nights...');
          await apiService.generatePingolfSeasonWeeks(seasonId);
          await apiService.generatePingolfHostNights(seasonId);
        }
      }

      showToast('Season created', 'success');
    } catch (error) {
      showToast(
        `Season created, but setup did not finish: ${error.response?.data?.error || 'something went wrong'}. Finish it on the season page.`,
        'warning'
      );
    } finally {
      setBusy(false);
      setProgressLabel('');
      navigate(`/pingolf-season/admin/${seasonId}`);
    }
  };

  const step = STEPS[stepIndex];
  const isLastStep = stepIndex === STEPS.length - 1;

  return (
    <div className="pl-admin-page bn-page-padding">
      <div className="pl-admin-hero" style={{ backgroundImage: `url(${pingolfHero})` }}>
        <div className="pl-admin-hero-overlay" />
        <div className="pl-admin-hero-content">
          <DSBackLink to="/pingolf-season/admin">All seasons</DSBackLink>
          <div className="pl-admin-hero-title-row">
            <h1>New season</h1>
          </div>
        </div>
      </div>

      <ol className="pl-admin-wizard-steps">
        {STEPS.map((entry, index) => (
          <li
            key={entry.key}
            className={`pl-admin-wizard-step${index === stepIndex ? ' is-current' : ''}${index < stepIndex ? ' is-done' : ''}`}
          >
            <span className="pl-admin-wizard-step-number">{index + 1}</span>
            <span className="pl-admin-wizard-step-title">{entry.title}</span>
          </li>
        ))}
      </ol>

      {step.key === 'basics' && (
        <>
          <DSCard className="pl-admin-create-season">
            <div className="pl-admin-week-section-title">Season number</div>
            <DSInput
              type="number"
              min="1"
              placeholder="Season number (e.g. 4)"
              value={seasonNumber}
              onChange={e => setSeasonNumber(e.target.value)}
            />
            <div className="pl-admin-create-season-preview">
              Will be created as <strong>PinGolf League Season {seasonNumber || 'N'}</strong>
            </div>

            <div className="pl-admin-scoring-mode">
              <div className="pl-admin-scoring-mode-label">Which card counts</div>
              {CARD_SCORING_MODES.map(mode => (
                <label key={mode.value} className="pl-admin-scoring-mode-option">
                  <input
                    type="radio"
                    name="card-scoring-mode"
                    value={mode.value}
                    checked={cardScoringMode === mode.value}
                    onChange={() => setCardScoringMode(mode.value)}
                  />
                  <span>
                    <strong>{mode.label}</strong>
                    <span className="pl-admin-scoring-mode-blurb">{mode.blurb}</span>
                  </span>
                </label>
              ))}
              <div className="pl-admin-scoring-mode-note">
                This is fixed once the season is created.
              </div>
            </div>
          </DSCard>

          <DSCard className="pl-admin-create-season">
            <div className="pl-admin-week-section-title">Season length</div>
            <div className="pl-admin-settings-fields">
              <DSInput
                type="number"
                min="1"
                label="Weeks played"
                value={weekCount}
                onChange={e => setWeekCount(e.target.value)}
              />
              <DSInput
                type="number"
                min="0"
                label="Worst weeks dropped"
                value={dropCount}
                onChange={e => setDropCount(e.target.value)}
              />
            </div>
            <div className="pl-admin-create-season-preview">
              {countedWeeks === null
                ? 'A player\'s season standing counts their best weeks after the drops.'
                : `Each player's best ${countedWeeks} of ${weekCount} weeks count toward the season standings.`}
            </div>
          </DSCard>
        </>
      )}

      {step.key === 'money' && (
        <>
          <DSCard className="pl-admin-create-season">
            <div className="pl-admin-week-section-title">Buy-in and payouts</div>
            <div className="pl-admin-settings-fields">
              <DSInput
                label="Card buy-in ($)"
                type="number"
                min="0"
                step="0.5"
                value={cardPriceDollars}
                onChange={e => setCardPriceDollars(e.target.value)}
                helpText="What a player pays for one scorecard."
              />
              <DSInput
                label="To the season pool, per card ($)"
                type="number"
                min="0"
                step="0.5"
                value={seasonPoolPerCard}
                onChange={e => setSeasonPoolPerCard(e.target.value)}
                helpText={`The rest goes to that week's pot - currently ${formatMoney(weeklyPerCard)} per card.`}
              />
              <DSInput
                label="Weekly break-even floor ($)"
                type="number"
                min="0"
                step="1"
                value={weeklyFloorDollars}
                onChange={e => setWeeklyFloorDollars(e.target.value)}
                helpText="Each paid place gets this back before the percentages apply, so a light night never costs a winner money. Set 0 to always split by percentage."
              />
              <DSSelect
                label="Weekly payout structure"
                value={weeklyPayoutPreset}
                onChange={e => setWeeklyPayoutPreset(e.target.value)}
                helpText="How each week's pot divides across the night's net board."
              >
                {structures.weekly.map(entry => (
                  <option key={entry.key} value={entry.key}>{entry.label}</option>
                ))}
              </DSSelect>
              <DSSelect
                label="Season payout structure"
                value={seasonPayoutStructure}
                onChange={e => setSeasonPayoutStructure(e.target.value)}
                helpText="How the season pool divides across the final standings."
              >
                {structures.season.map(entry => (
                  <option key={entry.key} value={entry.key}>{entry.label}</option>
                ))}
              </DSSelect>
            </div>
            <div className="pl-admin-scoring-mode-note">
              These stay editable on the season page until the first card is sold into the season.
            </div>
          </DSCard>

          <DSCard className="pl-admin-create-season">
            <div className="pl-admin-payout-columns">
              <div className="pl-admin-payout-block">
                <h4>Weekly pot</h4>
                {draftWeekly ? (
                  <ul className="pl-admin-payout-list">
                    {draftWeekly.shares.map((share, index) => (
                      <li key={PLACE_LABELS[index] || index}>
                        <span>{PLACE_LABELS[index] || `Place ${index + 1}`}</span>
                        <span>{percent(share)}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="pl-admin-field-help">Pick a weekly structure above.</p>
                )}
              </div>

              <div className="pl-admin-payout-block">
                <h4>Season pool</h4>
                {orderedSeasonSplit(draftSeasonStructure?.split).length > 0 ? (
                  <ul className="pl-admin-payout-list">
                    {orderedSeasonSplit(draftSeasonStructure.split).map(row => (
                      <li key={row.key}>
                        <span>{row.label}</span>
                        <span>{percent(row.share)}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="pl-admin-field-help">Pick a season structure above.</p>
                )}
              </div>
            </div>

            {draftWeekly && examplePot > 0 && (
              <p className="pl-admin-field-help">
                On a {exampleField}-card week the pot is {formatMoney(examplePot)}, paying{' '}
                {draftWeekly.shares.map(share => formatMoney(Math.floor(examplePot * share))).join(' / ')}{' '}
                before the floor applies.
              </p>
            )}
          </DSCard>
        </>
      )}

      {step.key === 'schedule' && (
        <>
          <DSCard className="pl-admin-create-season">
            <div className="pl-admin-week-section-title">Week 1 start date</div>
            <DSInput
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              helpText="Week 1 starts on this date. Every later week is scheduled 7 days further out."
            />
            <div className="pl-admin-scoring-mode-note">
              Optional. Leave it blank and set the schedule up on the season page later.
            </div>
          </DSCard>

          <DSCard className="pl-admin-create-season">
            <div className="pl-admin-week-section-title">Recurring host nights</div>
            {!startDate ? (
              <p className="pl-admin-empty">Pick a Week 1 start date above to add recurring host slots.</p>
            ) : (
              <>
                <ul className="pl-admin-session-list">
                  {slots.map(slot => (
                    <li key={slot.localId} className="pl-admin-session-item">
                      <div className="pl-admin-session-item-info">
                        <span className="pl-admin-session-item-title">
                          {DAY_NAMES[slot.dayOfWeek]} {formatTimeOfDay(slot.timeOfDay)} - {hostNameById(slot.hostUserId)}
                        </span>
                        <span className="pl-admin-session-item-date">{formatSlotDuration(slot.durationMinutes)}</span>
                      </div>
                      <DSButton appearance="text" tone="danger" size="sm" onClick={() => removeSlot(slot.localId)}>
                        Remove
                      </DSButton>
                    </li>
                  ))}
                  {slots.length === 0 && <li className="pl-admin-empty">No recurring host slots yet.</li>}
                </ul>

                <div className="pl-admin-session-add-row">
                  <DSButton appearance="outline" tone="neutral" size="sm" onClick={() => setShowSlotModal(true)}>
                    + Add recurring slot
                  </DSButton>
                </div>

                <p className="pl-admin-field-help">
                  With at least one slot, creating the season also builds every week with a
                  randomized machine lineup and puts a host night on the calendar for each week
                  and slot. Holes with no target score are flagged on the season page rather than
                  held back - each week&apos;s course snapshots into its nights once every target
                  is set, which has to happen before cards go on sale.
                </p>
              </>
            )}
          </DSCard>
        </>
      )}

      {step.key === 'review' && (
        <DSCard className="pl-admin-create-season">
          <div className="pl-admin-week-section-title">Review</div>

          <div className="pl-admin-readonly-row">
            <div className="pl-admin-readonly-field">
              <span className="pl-admin-readonly-label">Season</span>
              <span className="pl-admin-readonly-value">PinGolf League Season {seasonNumber || 'N'}</span>
            </div>
          </div>
          <div className="pl-admin-readonly-row">
            <div className="pl-admin-readonly-field">
              <span className="pl-admin-readonly-label">Which card counts</span>
              <span className="pl-admin-readonly-value">
                {CARD_SCORING_MODES.find(mode => mode.value === cardScoringMode)?.label}
              </span>
            </div>
          </div>
          <div className="pl-admin-readonly-row">
            <div className="pl-admin-readonly-field">
              <span className="pl-admin-readonly-label">Length</span>
              <span className="pl-admin-readonly-value">
                {countedWeeks === null
                  ? `${weekCount} weeks`
                  : `${weekCount} weeks, best ${countedWeeks} count`}
              </span>
            </div>
          </div>
          <div className="pl-admin-readonly-row">
            <div className="pl-admin-readonly-field">
              <span className="pl-admin-readonly-label">Buy-in</span>
              <span className="pl-admin-readonly-value">
                {formatMoney(cardPriceDollars)} - {formatMoney(weeklyPerCard)} to that week&apos;s pot,{' '}
                {formatMoney(seasonPoolPerCard)} to the season pool
              </span>
            </div>
          </div>
          <div className="pl-admin-readonly-row">
            <div className="pl-admin-readonly-field">
              <span className="pl-admin-readonly-label">Payouts</span>
              <span className="pl-admin-readonly-value">
                Weekly {draftWeekly?.label || weeklyPayoutPreset}, season{' '}
                {draftSeasonStructure?.label || seasonPayoutStructure},{' '}
                {formatMoney(weeklyFloorDollars)} floor
              </span>
            </div>
          </div>
          <div className="pl-admin-readonly-row">
            <div className="pl-admin-readonly-field">
              <span className="pl-admin-readonly-label">Schedule</span>
              <span className="pl-admin-readonly-value">
                {!startDate
                  ? 'Not set yet - finish this on the season page'
                  : slots.length === 0
                    ? `Week 1 on ${formatStartDate(startDate)}, no host slots yet`
                    : `Week 1 on ${formatStartDate(startDate)}, ${slots.length} host slot${slots.length === 1 ? '' : 's'}, weeks and host nights generated`}
              </span>
            </div>
          </div>

          {startDate && slots.length > 0 && (
            <ul className="pl-admin-session-list">
              {slots.map(slot => (
                <li key={slot.localId} className="pl-admin-session-item">
                  <div className="pl-admin-session-item-info">
                    <span className="pl-admin-session-item-title">
                      {DAY_NAMES[slot.dayOfWeek]} {formatTimeOfDay(slot.timeOfDay)} - {hostNameById(slot.hostUserId)}
                    </span>
                    <span className="pl-admin-session-item-date">{formatSlotDuration(slot.durationMinutes)}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </DSCard>
      )}

      <div className="pl-admin-create-season-actions">
        <DSButton
          variant="secondary"
          onClick={() => (stepIndex === 0 ? navigate('/pingolf-season/admin') : goBack())}
          disabled={busy}
        >
          {stepIndex === 0 ? 'Cancel' : 'Back'}
        </DSButton>
        {isLastStep ? (
          <DSButton onClick={createSeason} disabled={busy}>
            {busy ? (progressLabel || 'Creating...') : 'Create season'}
          </DSButton>
        ) : (
          <DSButton onClick={goNext} disabled={busy}>Next</DSButton>
        )}
      </div>

      {showSlotModal && (
        <BottomSheet
          title="Add recurring slot"
          onClose={() => setShowSlotModal(false)}
          footer={(
            <>
              <DSButton variant="secondary" onClick={() => setShowSlotModal(false)}>Cancel</DSButton>
              <DSButton onClick={addSlot}>Add recurring slot</DSButton>
            </>
          )}
        >
          <DSSelect id="pl-slot-day" label="Night of the week" value={slotDay} onChange={e => setSlotDay(e.target.value)}>
            {DAY_NAMES.map((name, index) => (
              <option key={name} value={index}>{name}</option>
            ))}
          </DSSelect>
          <DSInput id="pl-slot-time" label="Start time" type="time" value={slotTime} onChange={e => setSlotTime(e.target.value)} />
          <DSInput
            id="pl-slot-duration"
            label="Session length (minutes)"
            helpText="How long the night runs. 240 is a 4-hour night."
            type="number"
            min="30"
            step="30"
            value={slotDuration}
            onChange={e => setSlotDuration(e.target.value)}
          />
          <DSTypeaheadFilterSelect
            id="pl-slot-host"
            label="Host"
            placeholder="Search for a host..."
            emptyMessage="No hosts found"
            options={hosts.map(host => ({ value: host.id, label: hostDisplayName(host) }))}
            selectedValues={slotHostId ? [slotHostId] : []}
            onChange={(values) => setSlotHostId(values.length > 0 ? values[values.length - 1] : '')}
          />
        </BottomSheet>
      )}

      <PinGolfBottomNav />
    </div>
  );
}

export default PinGolfLeagueSeasonCreate;
